// src/components/admin/sections/AdminNews.tsx - VERSIÓN COMPLETA FUNCIONAL
import React, { useState, useEffect, useRef } from 'react';
import apiElCristo, { NewsItem, NewsData } from '../../../service/api_elcristo';
import './AdminNews.css';

const AdminNews: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [newsToDelete, setNewsToDelete] = useState<{ news: NewsItem, index: number } | null>(null);
  const [editingNews, setEditingNews] = useState<NewsItem | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isFeatured, setIsFeatured] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [uploadingImage, setUploadingImage] = useState<'main' | 'author' | null>(null);

  const titleEditorRef = useRef<HTMLDivElement>(null);

  const [newsData, setNewsData] = useState<NewsData>({
    subtitle: '',
    title: '',
    seeAllLink: '',
    featuredNews: {
      image: '',
      category: '',
      date: '',
      title: '',
      description: '',
      link: '',
      author: { name: '', image: '', role: '' }
    },
    additionalNews: []
  });

  const [formData, setFormData] = useState<NewsItem>({
    image: '',
    category: '',
    date: new Date().toISOString().split('T')[0],
    title: '',
    description: '',
    link: '',
    author: {
      name: 'Colegio El Cristo',
      image: '/assets/kidsa/assets/img/news/author.png',
      role: 'Administración'
    }
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (titleEditorRef.current && newsData.title) {
      titleEditorRef.current.innerHTML = newsData.title;
    }
  }, [newsData.title]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await apiElCristo.getNewsData();
      console.log('📥 News data:', data);
      setNewsData(data);
    } catch (error) {
      console.error('❌ Error loading:', error);
      setMessage({ type: 'error', text: 'Error al cargar noticias' });
    } finally {
      setLoading(false);
    }
  };

  const handleSectionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewsData(prev => ({ ...prev, [name]: value }));
  };

  const handleTitleInput = () => {
    if (titleEditorRef.current) {
      setNewsData(prev => ({ ...prev, title: titleEditorRef.current!.innerHTML }));
    }
  };

  const applyFormat = (editorRef: React.RefObject<HTMLDivElement>, tag: string) => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const selectedText = range.toString();

    if (selectedText) {
      const span = document.createElement('span');
      span.className = tag;
      span.textContent = selectedText;
      range.deleteContents();
      range.insertNode(span);
      
      if (editorRef === titleEditorRef) {
        setNewsData(prev => ({ ...prev, title: titleEditorRef.current!.innerHTML }));
      }
    }
  };

  const clearFormat = (editorRef: React.RefObject<HTMLDivElement>) => {
    if (!editorRef.current) return;
    
    const plainText = editorRef.current.innerText;
    editorRef.current.innerHTML = plainText;
    
    if (editorRef === titleEditorRef) {
      setNewsData(prev => ({ ...prev, title: plainText }));
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'main' | 'author') => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setMessage({ type: 'error', text: '❌ Solo PNG, JPG o WEBP' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    const maxSize = type === 'main' ? 5 : 2;
    if (file.size > maxSize * 1024 * 1024) {
      setMessage({ type: 'error', text: `❌ Máximo ${maxSize}MB` });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    try {
      setUploadingImage(type);
      
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('folder', 'news');
      
      const response = await fetch('https://backend.anubisai.net/api/elcristo/admin/upload/image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('elcristo_token')}`
        },
        body: formDataUpload
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al subir');
      }
      
      const data = await response.json();
      
      if (type === 'main') {
        setFormData(prev => ({ ...prev, image: data.url }));
      } else {
        setFormData(prev => ({
          ...prev,
          author: { ...prev.author, image: data.url }
        }));
      }
      
      setMessage({ type: 'success', text: '✅ Imagen subida' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err: any) {
      setMessage({ type: 'error', text: `❌ ${err.message}` });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setUploadingImage(null);
    }
  };

  const handleOpenModal = (news?: NewsItem, featured: boolean = false, index?: number) => {
    if (news) {
      setEditingNews(news);
      setEditingIndex(index !== undefined ? index : null);
      setFormData({ ...news });
      setIsFeatured(featured);
    } else {
      setEditingNews(null);
      setEditingIndex(null);
      setFormData({
        image: '',
        category: '',
        date: new Date().toISOString().split('T')[0],
        title: '',
        description: '',
        link: '',
        author: {
          name: 'Colegio El Cristo',
          image: '/assets/kidsa/assets/img/news/author.png',
          role: 'Administración'
        }
      });
      setIsFeatured(false);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingNews(null);
    setEditingIndex(null);
    setIsFeatured(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name.startsWith('author.')) {
      const authorField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        author: { ...prev.author, [authorField]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSaveNews = async () => {
    if (!formData.title || !formData.category || !formData.image) {
      setMessage({ type: 'error', text: '❌ Completa todos los campos' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    setSaving(true);

    try {
      let updatedData;

      if (isFeatured) {
        updatedData = { ...newsData, featuredNews: formData };
      } else {
        let updatedAdditional;
        if (editingIndex !== null) {
          updatedAdditional = [...newsData.additionalNews];
          updatedAdditional[editingIndex] = formData;
        } else {
          updatedAdditional = [...newsData.additionalNews, formData];
        }
        updatedData = { ...newsData, additionalNews: updatedAdditional };
      }

      console.log('📤 Guardando noticia:', updatedData);
      
      await apiElCristo.updateSiteContent({ news: updatedData });
      
      setNewsData(updatedData);
      handleCloseModal();
      
      setMessage({ type: 'success', text: '✅ Noticia guardada' });
      setTimeout(() => setMessage(null), 3000);
      
    } catch (error: any) {
      console.error('❌ Error:', error);
      setMessage({ type: 'error', text: `❌ ${error.message || 'Error al guardar'}` });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteModal = (news: NewsItem, index: number) => {
    setNewsToDelete({ news, index });
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setNewsToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!newsToDelete) return;

    setSaving(true);

    try {
      const updatedAdditional = newsData.additionalNews.filter((_, i) => i !== newsToDelete.index);
      const updatedData = { ...newsData, additionalNews: updatedAdditional };
      
      console.log('📤 Eliminando noticia:', updatedData);
      
      await apiElCristo.updateSiteContent({ news: updatedData });
      
      setNewsData(updatedData);
      handleCloseDeleteModal();
      
      setMessage({ type: 'success', text: '✅ Noticia eliminada' });
      setTimeout(() => setMessage(null), 3000);
      
    } catch (error: any) {
      console.error('❌ Error:', error);
      setMessage({ type: 'error', text: `❌ ${error.message}` });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleMoveNews = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newsData.additionalNews.length) return;

    const news = [...newsData.additionalNews];
    [news[index], news[newIndex]] = [news[newIndex], news[index]];
    setNewsData(prev => ({ ...prev, additionalNews: news }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      console.log('📤 Guardando cambios generales:', newsData);
      
      await apiElCristo.updateSiteContent({ news: newsData });
      
      setMessage({ type: 'success', text: '✅ Cambios guardados' });
      setTimeout(() => setMessage(null), 3000);
      
      await loadData();
    } catch (error: any) {
      console.error('❌ Error:', error);
      setMessage({ type: 'error', text: `❌ ${error.message || 'Error al guardar'}` });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-news-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-news-container">
      <div className="page-header">
        <div>
          <h1><i className="fas fa-newspaper"></i> Noticias & Comunicados</h1>
          <p>Administrar noticias del colegio</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => handleOpenModal()}>
          <i className="fas fa-plus"></i>
          Nueva Noticia
        </button>
      </div>

      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
          <i className={`fas fa-${message.type === 'success' ? 'check' : 'exclamation'}-circle`}></i>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="card">
          <div className="card-header">
            <h3><i className="fas fa-cog"></i> Configuración</h3>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label><i className="fas fa-tag"></i> Subtítulo</label>
              <input
                type="text"
                name="subtitle"
                value={newsData.subtitle}
                onChange={handleSectionChange}
                placeholder="Ej: Noticias Escolares"
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label><i className="fas fa-heading"></i> Título</label>
              
              <div className="editor-toolbar">
                <button type="button" className="toolbar-btn" onClick={() => applyFormat(titleEditorRef, 'elcristo-primary')}>
                  <i className="fas fa-font" style={{ color: '#ff0f00' }}></i> Rojo
                </button>
                <button type="button" className="toolbar-btn" onClick={() => applyFormat(titleEditorRef, 'elcristo-blue')}>
                  <i className="fas fa-font" style={{ color: '#70A6B1' }}></i> Azul
                </button>
                <button type="button" className="toolbar-btn" onClick={() => clearFormat(titleEditorRef)}>
                  <i className="fas fa-eraser"></i> Limpiar
                </button>
              </div>

              <div
                ref={titleEditorRef}
                className="rich-editor"
                contentEditable
                onInput={handleTitleInput}
                suppressContentEditableWarning
                data-placeholder="Título de la sección"
              />
            </div>

            <div className="form-group">
              <label><i className="fas fa-link"></i> Link "Ver Todas" (opcional)</label>
              <input
                type="text"
                name="seeAllLink"
                value={newsData.seeAllLink || ''}
                onChange={handleSectionChange}
                placeholder="/noticias o #todas-noticias"
                className="form-control"
              />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3><i className="fas fa-star"></i> Noticia Destacada</h3>
            <button type="button" className="btn btn-secondary" onClick={() => handleOpenModal(newsData.featuredNews, true)}>
              <i className="fas fa-edit"></i>
              Editar
            </button>
          </div>
          <div className="card-body">
            <div className="news-card featured">
              <div className="news-image">
                <img 
                  src={newsData.featuredNews.image.startsWith('http') 
                    ? newsData.featuredNews.image 
                    : `${window.location.origin}${newsData.featuredNews.image}`
                  } 
                  alt={newsData.featuredNews.title} 
                />
                <span className="news-category">{newsData.featuredNews.category}</span>
              </div>
              <div className="news-content">
                <div className="news-meta">
                  <i className="fas fa-calendar"></i>
                  {new Date(newsData.featuredNews.date).toLocaleDateString('es-CL')}
                </div>
                <h4>{newsData.featuredNews.title}</h4>
                <p>{newsData.featuredNews.description}</p>
                <div className="news-author">
                  <img 
                    src={newsData.featuredNews.author.image.startsWith('http') 
                      ? newsData.featuredNews.author.image 
                      : `${window.location.origin}${newsData.featuredNews.author.image}`
                    } 
                    alt={newsData.featuredNews.author.name} 
                  />
                  <div>
                    <strong>{newsData.featuredNews.author.name}</strong>
                    <span>{newsData.featuredNews.author.role}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3><i className="fas fa-list"></i> Noticias Adicionales ({newsData.additionalNews.length})</h3>
          </div>
          <div className="card-body">
            {newsData.additionalNews.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-newspaper"></i>
                <p>No hay noticias adicionales</p>
                <button type="button" className="btn btn-primary" onClick={() => handleOpenModal()}>
                  <i className="fas fa-plus"></i>
                  Agregar Noticia
                </button>
              </div>
            ) : (
              <div className="news-grid">
                {newsData.additionalNews.map((news, index) => (
                  <div key={index} className="news-card">
                    <div className="news-card-drag">
                      <i className="fas fa-grip-vertical"></i>
                    </div>
                    
                    <div className="news-image">
                      <img 
                        src={news.image.startsWith('http') 
                          ? news.image 
                          : `${window.location.origin}${news.image}`
                        } 
                        alt={news.title} 
                      />
                      <span className="news-category">{news.category}</span>
                    </div>
                    
                    <div className="news-content">
                      <div className="news-meta">
                        <i className="fas fa-calendar"></i>
                        {new Date(news.date).toLocaleDateString('es-CL')}
                      </div>
                      <h4>{news.title}</h4>
                      <p>{news.description}</p>
                    </div>
                    
                    <div className="news-card-actions">
                      <button type="button" className="btn-icon" onClick={() => handleMoveNews(index, 'up')} disabled={index === 0}>
                        <i className="fas fa-arrow-up"></i>
                      </button>
                      <button type="button" className="btn-icon" onClick={() => handleMoveNews(index, 'down')} disabled={index === newsData.additionalNews.length - 1}>
                        <i className="fas fa-arrow-down"></i>
                      </button>
                      <button type="button" className="btn-icon btn-edit" onClick={() => handleOpenModal(news, false, index)}>
                        <i className="fas fa-edit"></i>
                      </button>
                      <button type="button" className="btn-icon btn-delete" onClick={() => handleOpenDeleteModal(news, index)}>
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={loadData} disabled={saving}>
            <i className="fas fa-undo"></i> Recargar
          </button>

          <button type="submit" className="btn btn-primary" disabled={saving || uploadingImage !== null}>
            {saving ? (
              <><div className="spinner-small"></div> Guardando...</>
            ) : (
              <><i className="fas fa-save"></i> Guardar Cambios</>
            )}
          </button>
        </div>
      </form>

      {/* PREVIEW SECTION */}
      <div className="news-preview-section">
        <div className="news-preview-header">
          <h3><i className="fas fa-eye"></i> Vista Previa</h3>
        </div>
        
        <div className="news-preview">
          <div className="preview-section-title">
            <span className="preview-subtitle">{newsData.subtitle || 'Subtítulo'}</span>
            <h2 
              className="preview-title"
              dangerouslySetInnerHTML={{ 
                __html: newsData.title || 'Explora nuestras <span class="elcristo-primary">noticias</span>' 
              }}
            />
            {newsData.seeAllLink && (
              <a href={newsData.seeAllLink} className="preview-see-all">
                Ver todas <i className="fas fa-arrow-right"></i>
              </a>
            )}
          </div>

          <div className="preview-news-wrapper">
            <div className="preview-featured-news">
              <div className="preview-news-image">
                <img 
                  src={newsData.featuredNews.image.startsWith('http') 
                    ? newsData.featuredNews.image 
                    : `${window.location.origin}${newsData.featuredNews.image}`
                  } 
                  alt={newsData.featuredNews.title} 
                />
              </div>
              <div className="preview-news-content">
                <div className="preview-news-meta">
                  <span className="preview-category">
                    <i className="fas fa-tag"></i> {newsData.featuredNews.category}
                  </span>
                  <span className="preview-date">
                    <i className="fas fa-calendar"></i> {new Date(newsData.featuredNews.date).toLocaleDateString('es-CL')}
                  </span>
                </div>
                <h3>{newsData.featuredNews.title}</h3>
                <p>{newsData.featuredNews.description}</p>
                <div className="preview-author">
                  <img 
                    src={newsData.featuredNews.author.image.startsWith('http') 
                      ? newsData.featuredNews.author.image 
                      : `${window.location.origin}${newsData.featuredNews.author.image}`
                    } 
                    alt={newsData.featuredNews.author.name} 
                  />
                  <div>
                    <span className="preview-author-role">Por {newsData.featuredNews.author.role}</span>
                    <h6>{newsData.featuredNews.author.name}</h6>
                  </div>
                </div>
              </div>
            </div>

            <div className="preview-additional-news">
              {(newsData.additionalNews.length > 0 ? newsData.additionalNews.slice(0, 3) : [
                { image: '/assets/kidsa/assets/img/news/02.jpg', category: 'Tecnología', date: '2024-01-15', title: 'Noticia 1', description: 'Descripción...', link: '#', author: { name: 'Admin', image: '/assets/kidsa/assets/img/news/author.png', role: 'Admin' } },
                { image: '/assets/kidsa/assets/img/news/03.jpg', category: 'Deportes', date: '2024-01-14', title: 'Noticia 2', description: 'Descripción...', link: '#', author: { name: 'Admin', image: '/assets/kidsa/assets/img/news/author.png', role: 'Admin' } },
                { image: '/assets/kidsa/assets/img/news/04.jpg', category: 'Cultura', date: '2024-01-13', title: 'Noticia 3', description: 'Descripción...', link: '#', author: { name: 'Admin', image: '/assets/kidsa/assets/img/news/author.png', role: 'Admin' } }
              ]).map((news, index) => (
                <div key={index} className="preview-news-item">
                  <div className="preview-news-thumb">
                    <img 
                      src={news.image.startsWith('http') 
                        ? news.image 
                        : `${window.location.origin}${news.image}`
                      } 
                      alt={news.title} 
                    />
                  </div>
                  <div className="preview-news-details">
                    <div className="preview-news-meta">
                      <span className="preview-category">
                        <i className="fas fa-tag"></i> {news.category}
                      </span>
                      <span className="preview-date">
                        <i className="fas fa-calendar"></i> {new Date(news.date).toLocaleDateString('es-CL')}
                      </span>
                    </div>
                    <h4>{news.title}</h4>
                    <div className="preview-author-small">
                      <img 
                        src={news.author.image.startsWith('http') 
                          ? news.author.image 
                          : `${window.location.origin}${news.author.image}`
                        } 
                        alt={news.author.name} 
                      />
                      <div>
                        <span>Por {news.author.role}</span>
                        <h6>{news.author.name}</h6>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* MODAL CREAR/EDITAR */}
      {showModal && (
        <div className="team-modal-overlay">
          <div className="team-modal-container" style={{ maxWidth: '800px' }}>
            <div className="team-modal-header">
              <h3>
                <i className="fas fa-newspaper"></i>
                {isFeatured ? 'Editar Destacada' : editingNews ? 'Editar Noticia' : 'Nueva Noticia'}
              </h3>
              <button type="button" className="team-modal-close" onClick={handleCloseModal} disabled={saving}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="team-modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Título *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Título de la noticia"
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label>Categoría *</label>
                  <input
                    type="text"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    placeholder="Tecnología, Deportes..."
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Descripción</label>
                <textarea
                  name="description"
                  value={formData.description || ''}
                  onChange={handleChange}
                  placeholder="Descripción breve"
                  rows={3}
                  className="form-control"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Fecha *</label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label>Link (opcional)</label>
                  <input
                    type="text"
                    name="link"
                    value={formData.link}
                    onChange={handleChange}
                    placeholder="#noticias"
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Imagen Principal *</label>
                
                {formData.image && (
                  <div className="image-preview-modal">
                    <img 
                      src={formData.image.startsWith('http') 
                        ? formData.image 
                        : `${window.location.origin}${formData.image}`
                      } 
                      alt="Preview" 
                    />
                  </div>
                )}
                
                <div className="image-upload-group">
                  <input
                    type="text"
                    name="image"
                    value={formData.image}
                    onChange={handleChange}
                    placeholder="/assets/kidsa/assets/img/news/01.jpg"
                    className="form-control"
                  />
                  
                  <button
                    type="button"
                    className="btn btn-upload"
                    disabled={uploadingImage === 'main'}
                    onClick={() => document.getElementById('news-img')?.click()}
                  >
                    {uploadingImage === 'main' ? (
                      <><div className="spinner-small"></div> Subiendo...</>
                    ) : (
                      <><i className="fas fa-upload"></i> Subir</>
                    )}
                  </button>
                  <input
                    id="news-img"
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'main')}
                    style={{ display: 'none' }}
                  />
                </div>
                <small className="hint">
                  <i className="fas fa-info-circle"></i> PNG, JPG, WEBP (máx 5MB) - Horizontal 16:9
                </small>
              </div>

              <hr style={{ margin: '25px 0', border: 'none', borderTop: '2px solid #e0e0e0' }} />

              <h4 style={{ marginBottom: '15px', fontSize: '16px', fontWeight: '600', color: '#333' }}>
                <i className="fas fa-user"></i> Información del Autor
              </h4>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Nombre</label>
                  <input
                    type="text"
                    name="author.name"
                    value={formData.author.name}
                    onChange={handleChange}
                    placeholder="Nombre del autor"
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label>Rol/Cargo</label>
                  <input
                    type="text"
                    name="author.role"
                    value={formData.author.role || ''}
                    onChange={handleChange}
                    placeholder="Directora, Profesor..."
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Foto del Autor</label>
                
                {formData.author.image && (
                  <div className="author-preview-modal">
                    <img 
                      src={formData.author.image.startsWith('http') 
                        ? formData.author.image 
                        : `${window.location.origin}${formData.author.image}`
                      } 
                      alt="Author" 
                    />
                  </div>
                )}
                
                <div className="image-upload-group">
                  <input
                    type="text"
                    name="author.image"
                    value={formData.author.image}
                    onChange={handleChange}
                    placeholder="/assets/kidsa/assets/img/news/author.png"
                    className="form-control"
                  />
                  
                  <button
                    type="button"
                    className="btn btn-upload"
                    disabled={uploadingImage === 'author'}
                    onClick={() => document.getElementById('author-img')?.click()}
                  >
                    {uploadingImage === 'author' ? (
                      <><div className="spinner-small"></div> Subiendo...</>
                    ) : (
                      <><i className="fas fa-upload"></i> Subir</>
                    )}
                  </button>
                  <input
                    id="author-img"
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'author')}
                    style={{ display: 'none' }}
                  />
                </div>
                <small className="hint">Foto circular (máx 2MB)</small>
              </div>
            </div>

            <div className="team-modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleCloseModal} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="btn btn-primary" onClick={handleSaveNews} disabled={saving}>
                {saving ? (
                  <><div className="spinner-small"></div> Guardando...</>
                ) : (
                  <>{editingNews ? 'Actualizar' : 'Agregar'}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DELETE */}
      {showDeleteModal && newsToDelete && (
        <div className="team-modal-overlay">
          <div className="team-modal-container" style={{ maxWidth: '500px' }}>
            <div className="team-modal-header">
              <h3>
                <i className="fas fa-exclamation-triangle" style={{ color: '#ff0f00' }}></i>
                Confirmar Eliminación
              </h3>
              <button type="button" className="team-modal-close" onClick={handleCloseDeleteModal} disabled={saving}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="team-modal-body">
              <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                ¿Eliminar noticia <strong>"{newsToDelete.news.title}"</strong>?
              </p>
              <p style={{ fontSize: '14px', color: '#666' }}>
                No se puede deshacer.
              </p>
            </div>

            <div className="team-modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleCloseDeleteModal} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="btn btn-danger" onClick={handleConfirmDelete} disabled={saving}>
                {saving ? (
                  <><div className="spinner-small"></div> Eliminando...</>
                ) : (
                  <><i className="fas fa-trash"></i> Eliminar</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNews;